import { useEffect, useRef, useCallback } from 'react';
import { Channel } from 'pusher-js';
import { PusherConfig } from 'shared/constants/pusher';
import usePusher from 'src/hooks/use-pusher';

type PublicContextSessionData = {
  id: string;
  status: string;
  loginMethodVerified: boolean | null;
  claimedByUserId?: string;
  expiresAt: string;
};

type UsePublicContextSessionPusherProps = {
  sessionId: string;
  onSessionUpdated?: (data: PublicContextSessionData) => void;
  onLoginMethodVerified?: (data: { id: string; loginMethodVerified: boolean | null }) => void;
  onSessionClaimed?: (data: { id: string; status: string; claimedByUserId?: string; claimedAt?: string }) => void;
  onSessionExpired?: (data: { id: string; status: string }) => void;
  enabled?: boolean;
};

export function usePublicContextSessionPusher({
  sessionId,
  onSessionUpdated,
  onLoginMethodVerified,
  onSessionClaimed,
  onSessionExpired,
  enabled = true,
}: UsePublicContextSessionPusherProps): { subscribe: () => void; unsubscribe: () => void } {
  const pusher = usePusher();
  const channelRef = useRef<Channel | null>(null);
  const channelNameRef = useRef<string | null>(null);

  // Store latest handlers in refs to avoid dependency array issues
  const handlersRef = useRef({
    onSessionUpdated,
    onLoginMethodVerified,
    onSessionClaimed,
    onSessionExpired,
  });

  // Update refs when handlers change
  useEffect(() => {
    handlersRef.current = {
      onSessionUpdated,
      onLoginMethodVerified,
      onSessionClaimed,
      onSessionExpired,
    };
  });

  const subscribe = useCallback(() => {
    if (!enabled || !sessionId) {
      return;
    }

    // Don't subscribe if already subscribed to the same channel
    if (channelRef.current && channelNameRef.current === PusherConfig.PublicContextSession.CHANNEL(sessionId)) {
      return;
    }

    // Unsubscribe from previous channel if exists
    if (channelRef.current && channelNameRef.current) {
      try {
        // Unbind all events
        channelRef.current.unbind(PusherConfig.PublicContextSession.Events.SESSION_UPDATED);
        channelRef.current.unbind(PusherConfig.PublicContextSession.Events.LOGIN_METHOD_VERIFIED);
        channelRef.current.unbind(PusherConfig.PublicContextSession.Events.SESSION_CLAIMED);
        channelRef.current.unbind(PusherConfig.PublicContextSession.Events.SESSION_EXPIRED);

        // Unsubscribe from previous channel
        pusher.unsubscribe(channelNameRef.current);
      } catch (error) {
        // Ignore unsubscribe errors - channel might already be unsubscribed
        console.warn('Pusher unsubscribe error:', error);
      }
    }

    const channelName = PusherConfig.PublicContextSession.CHANNEL(sessionId);
    const channel = pusher.subscribe(channelName);

    // Bind to session updated event
    if (handlersRef.current.onSessionUpdated) {
      channel.bind(PusherConfig.PublicContextSession.Events.SESSION_UPDATED, handlersRef.current.onSessionUpdated);
    }

    // Bind to login method verified event
    if (handlersRef.current.onLoginMethodVerified) {
      channel.bind(
        PusherConfig.PublicContextSession.Events.LOGIN_METHOD_VERIFIED,
        handlersRef.current.onLoginMethodVerified
      );
    }

    // Bind to session claimed event
    if (handlersRef.current.onSessionClaimed) {
      channel.bind(PusherConfig.PublicContextSession.Events.SESSION_CLAIMED, handlersRef.current.onSessionClaimed);
    }

    // Bind to session expired event
    if (handlersRef.current.onSessionExpired) {
      channel.bind(PusherConfig.PublicContextSession.Events.SESSION_EXPIRED, handlersRef.current.onSessionExpired);
    }

    channelRef.current = channel;
    channelNameRef.current = channelName;
  }, [sessionId, enabled, pusher]);

  const unsubscribe = useCallback(() => {
    if (channelRef.current && channelNameRef.current) {
      try {
        // Unbind all events
        channelRef.current.unbind(PusherConfig.PublicContextSession.Events.SESSION_UPDATED);
        channelRef.current.unbind(PusherConfig.PublicContextSession.Events.LOGIN_METHOD_VERIFIED);
        channelRef.current.unbind(PusherConfig.PublicContextSession.Events.SESSION_CLAIMED);
        channelRef.current.unbind(PusherConfig.PublicContextSession.Events.SESSION_EXPIRED);

        // Unsubscribe from channel using stored channel name
        pusher.unsubscribe(channelNameRef.current);
      } catch (error) {
        // Ignore unsubscribe errors - channel might already be unsubscribed
        console.warn('Pusher unsubscribe error:', error);
      }

      channelRef.current = null;
      channelNameRef.current = null;
    }
  }, [pusher]);

  useEffect(() => {
    subscribe();

    return unsubscribe;
  }, [subscribe, unsubscribe]);

  return {
    subscribe,
    unsubscribe,
  };
}
